/**
 * Realtime desktop URL construction — pure helpers (2026-08-03).
 *
 * Split out of useRealtimeDesktopTarget so the URL contract is unit-testable
 * without booting firebase. No firebase/react imports may be added here.
 *
 * HISTORY — why the base URL is now echoed verbatim: this module used to run a
 * client-side "stale host" rewrite table that remapped ~12 hardcoded IPs onto a
 * hardcoded Fernando host. By the time it was removed, the rewrite TARGET
 * (35.238.149.88) was itself a RELEASED ephemeral GCP IP — and because sslip.io
 * resolves any dashed-IP hostname to that IP with no ownership check, whoever
 * held it could serve content that got iframed inside an authenticated
 * prest0.ai/desktop page carrying the viewer's accountId and uid. The table also
 * listed the LIVE internal VM IP (self-loop), and whenever it fired it silently
 * killed AvatarBridge, whose allowed origin derives from the UN-rewritten
 * baseUrl. Stale-host correction belongs server-side: scripts/rollover-vm-ip.js
 * rewrites every URL field on an IP move (selfHealDedicatedVmRoutingDocs only
 * repairs STRUCTURALLY broken docs — a stale-but-well-formed host is not in
 * its remit, so the rollover script is the real mechanism).
 */

export function trimTrailingSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * Constrain what /desktop is willing to embed for an account.
 *
 * accounts/{id}/vm/main is NOT server-only — firestore.rules allows the
 * account owner to write it — so this doc is semi-trusted input that ends up
 * as an iframe src inside an authenticated page carrying accountId + uid. A
 * scheme check alone would still admit any https host, so mirror the SHAPE the
 * server actually writes (routingDocs.ts builds `https://${dashed-ip}.sslip.io`
 * and validates it with the same dashed-sslip pattern): https, a
 * dashed-IP sslip.io host or a prest0.ai host, and no embedded credentials.
 *
 * Applied ONLY to the Firestore-sourced account branch; the internal branch
 * keeps its `window.PREST0_REALTIME_INTERNAL_BASE` staging override.
 */
const DASHED_SSLIP_HOST = /^\d{1,3}(?:-\d{1,3}){3}\.sslip\.io$/i;

export function isSafeAccountVmBase(baseUrl: string): boolean {
  try {
    const u = new URL(baseUrl);
    if (u.protocol !== 'https:') return false;
    if (u.username || u.password) return false;
    return DASHED_SSLIP_HOST.test(u.hostname)
      || u.hostname === 'prest0.ai'
      || u.hostname.endsWith('.prest0.ai');
  } catch {
    return false;
  }
}

/**
 * Per-page-load cache buster. The realtime app HTML is edited live on each VM
 * (Caddy serves `/public/preston-realtime.html` directly from disk), so a short
 * fingerprint that changes per session forces a fresh fetch on every /desktop
 * visit. Bucketed to ~30s so refreshes within one conversation reuse bfcache.
 */
export function realtimeCacheBuster(nowMs: number = Date.now()): string {
  return Math.floor(nowMs / 30000).toString(36);
}

/** Compose the iframe URL. The host is echoed verbatim — see file header. */
export function buildTargetUrl(baseUrl: string, accountId: string, uid: string): string {
  const cleanBase = trimTrailingSlash(baseUrl);
  const params = new URLSearchParams({
    platform: 'prest0',
    accountId,
    uid,
    v: realtimeCacheBuster(),
  });
  return `${cleanBase}/public/preston-realtime.html?${params.toString()}`;
}
